import { useState } from "react";
import { Layout } from "@/components/Layout";
import { api, API } from "@/lib/api";
import { FOOD_GROUPS, NUTRITION_TEMPLATES } from "@/data/foods";
import { Calculator, ForkKnife, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { REQUIRES } from "@/lib/plans";
import { PlanGate } from "@/components/PlanGate";
import { UnitToggle, toCm, toKg } from "@/components/UnitToggle";

const TABS = [
  { id: "calculator", label: "Calculadora" },
  { id: "templates", label: "Plantillas" },
  { id: "foods", label: "Sustitución" },
];

export default function Nutrition() {
  const [tab, setTab] = useState("calculator");

  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="as-label text-as-green mb-3">MÓDULO 3 · NUTRICIÓN</p>
          <h1 className="as-heading text-5xl text-white">
            COME PARA <span className="text-as-green">TRANSFORMAR.</span>
          </h1>
        </div>
      </section>

      <div className="border-b border-as-border bg-as-surface sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`py-4 as-label whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id ? "border-as-green text-as-green" : "border-transparent text-as-gray hover:text-white"
              }`}
              data-testid={`nutrition-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <section className="bg-as-surface py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {tab === "calculator" && (
            <PlanGate required={REQUIRES.NUTRITION_CALCULATOR} planName="AS EVOLUTION">
              <Calculator2 />
            </PlanGate>
          )}
          {tab === "templates" && (
            <PlanGate required={REQUIRES.NUTRITION_TEMPLATES} planName="AS START">
              <Templates />
            </PlanGate>
          )}
          {tab === "foods" && (
            <PlanGate required={REQUIRES.NUTRITION_FOODS} planName="AS START">
              <FoodList />
            </PlanGate>
          )}
        </div>
      </section>
    </Layout>
  );
}

const Calculator2 = () => {
  const [form, setForm] = useState({
    age: 28, gender: "male",
    height: 175, height_unit: "cm",   // "cm" | "in"
    weight: 75, weight_unit: "kg",    // "kg" | "lb"
    activity_level: "moderate", goal: "fat_loss",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const calculate = async () => {
    const h = parseFloat(form.height);
    const w = parseFloat(form.weight);
    const a = parseInt(form.age, 10);
    if (!h || h <= 0 || !w || w <= 0 || !a || a <= 0) {
      toast.error("Ingresa edad, altura y peso válidos");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/nutrition/macros", {
        age: a,
        gender: form.gender,
        height_cm: toCm(h, form.height_unit),
        weight_kg: toKg(w, form.weight_unit),
        activity_level: form.activity_level,
        goal: form.goal,
      });
      setResult(data);
    } catch {
      toast.error("Error al calcular");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!result) return;
    try {
      const res = await fetch(`${API}/nutrition/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_name: "Cliente AS TRAINING",
          goal: form.goal,
          calories: result.calories,
          protein_g: result.protein_g,
          carbs_g: result.carbs_g,
          fat_g: result.fat_g,
          meals: NUTRITION_TEMPLATES[form.goal]?.meals || [],
        }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Error al generar PDF");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="as-card">
        <div className="flex items-center gap-3 mb-6">
          <Calculator size={28} weight="duotone" color="#39FF14" />
          <h2 className="as-heading text-2xl text-white">CALCULADORA DE MACROS</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Edad">
            <input className="as-input" type="number" value={form.age} onChange={(e) => upd("age", e.target.value)} data-testid="macro-age" />
          </Field>
          <Field label="Género">
            <select className="as-input" value={form.gender} onChange={(e) => upd("gender", e.target.value)} data-testid="macro-gender">
              <option value="male">Masculino</option>
              <option value="female">Femenino</option>
            </select>
          </Field>
          <Field label="Altura">
            <div className="flex gap-2">
              <input
                className="as-input flex-1"
                type="number"
                value={form.height}
                onChange={(e) => upd("height", e.target.value)}
                data-testid="macro-height"
              />
              <UnitToggle
                value={form.height_unit}
                onChange={(u) => upd("height_unit", u)}
                options={[{ v: "cm", l: "CM" }, { v: "in", l: "IN" }]}
                testid="macro-height-unit"
              />
            </div>
          </Field>
          <Field label="Peso">
            <div className="flex gap-2">
              <input
                className="as-input flex-1"
                type="number"
                value={form.weight}
                onChange={(e) => upd("weight", e.target.value)}
                data-testid="macro-weight"
              />
              <UnitToggle
                value={form.weight_unit}
                onChange={(u) => upd("weight_unit", u)}
                options={[{ v: "kg", l: "KG" }, { v: "lb", l: "LB" }]}
                testid="macro-weight-unit"
              />
            </div>
          </Field>
          <Field label="Nivel actividad">
            <select className="as-input" value={form.activity_level} onChange={(e) => upd("activity_level", e.target.value)}>
              <option value="sedentary">Sedentario</option>
              <option value="light">Ligero</option>
              <option value="moderate">Moderado</option>
              <option value="active">Activo</option>
              <option value="very_active">Muy activo</option>
            </select>
          </Field>
          <Field label="Objetivo">
            <select className="as-input" value={form.goal} onChange={(e) => upd("goal", e.target.value)} data-testid="macro-goal">
              <option value="fat_loss">Pérdida de grasa</option>
              <option value="maintenance">Mantenimiento</option>
              <option value="muscle_gain">Ganancia muscular</option>
              <option value="recomposition">Recomposición</option>
            </select>
          </Field>
        </div>
        <button onClick={calculate} disabled={loading} className="as-btn-primary w-full mt-6" data-testid="calc-macros-btn">
          {loading ? "Calculando..." : "Calcular"}
        </button>
      </div>

      <div className="as-card">
        <h2 className="as-heading text-2xl text-white mb-4">RESULTADO</h2>
        {!result ? (
          <p className="text-as-gray text-sm">Completa el formulario para ver tus macros.</p>
        ) : (
          <div className="space-y-4">
            <Stat label="BMR" value={`${result.bmr} kcal`} />
            <Stat label="TDEE" value={`${result.tdee} kcal`} />
            <Stat label="Calorías objetivo" value={`${result.calories} kcal`} highlight />
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-as-border">
              <Macro label="Proteínas" value={`${result.protein_g}g`} />
              <Macro label="Carbos" value={`${result.carbs_g}g`} />
              <Macro label="Grasas" value={`${result.fat_g}g`} />
            </div>
            <button onClick={downloadPDF} className="as-btn-primary w-full mt-4" data-testid="download-macros-pdf">
              Descargar plan PDF <DownloadSimple size={18} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Templates = () => {
  // Any paid plan: show all 3 templates (paywall handled by parent PlanGate).
  const entries = Object.entries(NUTRITION_TEMPLATES);
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {entries.map(([key, tpl]) => (
        <div key={key} className="as-card flex flex-col" data-testid={`template-${key}`}>
          <ForkKnife size={32} weight="duotone" color="#39FF14" className="mb-3" />
          <h3 className="as-heading text-2xl text-white mb-2">{tpl.title.toUpperCase()}</h3>
          <p className="text-sm text-as-gray mb-4">{tpl.description}</p>
          <div className="space-y-2 mt-2 flex-1">
            {tpl.meals.map((m) => (
              <div key={m.name} className="border-l-2 border-as-green pl-3 py-1">
                <p className="text-white text-sm font-semibold">{m.name}</p>
                <p className="text-xs text-as-gray">{m.foods}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const FoodList = () => {
  const [q, setQ] = useState("");
  const filtered = FOOD_GROUPS.map((g) => ({
    ...g,
    foods: g.foods.filter((f) => f.name.toLowerCase().includes(q.toLowerCase())),
  }));
  return (
    <div>
      <div className="mb-6 relative max-w-md">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-as-gray" />
        <input
          className="as-input pl-10"
          placeholder="Buscar alimento..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="food-search"
        />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        {filtered.map((g) => g.foods.length > 0 && (
          <div key={g.name} className="as-card">
            <h3 className="as-heading text-xl mb-4" style={{ color: g.color }}>{g.name.toUpperCase()}</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left as-label">
                  <th className="py-2">Alimento</th>
                  <th className="py-2">Porción</th>
                  <th className="py-2 text-right">Kcal</th>
                  <th className="py-2 text-right">P/C/G</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-as-border">
                {g.foods.map((f) => (
                  <tr key={f.name}>
                    <td className="py-2 text-white">{f.name}</td>
                    <td className="py-2 text-as-gray">{f.portion}</td>
                    <td className="py-2 text-right text-as-green font-semibold">{f.kcal}</td>
                    <td className="py-2 text-right text-as-gray text-xs">{f.protein}/{f.carbs}/{f.fat}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="as-label block mb-2">{label}</span>
    {children}
  </label>
);

const Stat = ({ label, value, highlight }) => (
  <div className="flex justify-between items-center py-2">
    <span className="as-label">{label}</span>
    <span className={`as-heading text-2xl ${highlight ? "text-as-green" : "text-white"}`}>{value}</span>
  </div>
);
const Macro = ({ label, value }) => (
  <div className="text-center">
    <p className="as-label">{label}</p>
    <p className="as-heading text-2xl text-white mt-1">{value}</p>
  </div>
);
